import React, { useCallback } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { Else, If, Then } from 'react-if'
import { Placeholder } from '@/atoms/Placeholder'
import { useTellYourStoryPrompt } from '@/organisms/TellYourStoryPrompt/TellYourStoryPrompt'
import { Offer } from '@/services/PifService'
import { getStripe } from '@/services/StripeService/StripePaymentService'
import { useUser } from '@/services/UserService'
import { useLocale } from '@/utils/LocaleUtil'
import { useClientSecret } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useClientSecret'
import { PayItForwardFormProvider } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm/PayItForwardFormContext'
import { usePayItForwardState } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/usePayItForwardState'
import { TheatricalPifExpressCheckoutStripeForm } from '@/views/PayItForward/TheatricalPayItForwardView/components/TheatricalPifExpressCheckoutStripeForm'

export interface TheatricalPifExpressPaymentOPtionsProps {
  projectSlug: string
  offer: Offer
  isShowThanksForSharing: boolean
  isGuest: boolean
  onComplete?: () => void
  shouldOnlyShowCreditCardForm?: boolean
  setShouldOnlyShowCreditCardForm?: React.Dispatch<React.SetStateAction<boolean>>
  disabled?: boolean
}

export const TheatricalPifExpressPaymentOptions: React.FC<TheatricalPifExpressPaymentOPtionsProps> = ({
  projectSlug,
  offer,
  isShowThanksForSharing,
  isGuest,
  onComplete,
  shouldOnlyShowCreditCardForm = false,
  setShouldOnlyShowCreditCardForm,
  disabled = false,
}) => {
  const { isSubscription, onComplete: onCompletePifState } = usePayItForwardState()
  const { openModal: openTellYourStoryModal, setRelatedOrderId } = useTellYourStoryPrompt()
  const { user } = useUser()
  const { clientSecret } = useClientSecret({ offer })
  const { locale } = useLocale()

  const openTellYourStoryModalWhenApplicable = useCallback(() => {
    if (isShowThanksForSharing && Boolean(user)) {
      openTellYourStoryModal()
    }
  }, [isShowThanksForSharing, openTellYourStoryModal, user])

  const onFormComplete = useCallback(
    ({ orderId }: { orderId: string }) => {
      onCompletePifState()
      setRelatedOrderId(orderId)
      openTellYourStoryModalWhenApplicable()
      onComplete?.()
    },
    [onCompletePifState, openTellYourStoryModalWhenApplicable, setRelatedOrderId, onComplete],
  )

  return (
    <Elements stripe={getStripe(locale)} options={{ loader: 'auto' }}>
      <PayItForwardFormProvider
        onComplete={onFormComplete}
        offer={offer}
        projectSlug={projectSlug}
        isSubscription={isSubscription}
        user={user}
      >
        <If condition={clientSecret}>
          <Then>
            {clientSecret && (
              <TheatricalPifExpressCheckoutStripeForm
                disabled={disabled}
                clientSecret={clientSecret}
                isGuest={isGuest}
                offer={offer}
                isSubscription={isSubscription}
                projectSlug={projectSlug}
                shouldOnlyShowCreditCardForm={shouldOnlyShowCreditCardForm}
                setShouldOnlyShowCreditCardForm={setShouldOnlyShowCreditCardForm}
              />
            )}
          </Then>
          <Else>
            <div className="flex flex-col gap-3">
              {shouldOnlyShowCreditCardForm ? (
                <div className="flex flex-col gap-4">
                  <Placeholder placeholderClassName="bg-core-gray-200" className="h-8 w-full rounded bg-gray-500" />
                  <Placeholder placeholderClassName="bg-core-gray-200" className="h-8 w-full rounded bg-gray-500" />
                  <div className="flex flex-col gap-2">
                    <Placeholder
                      placeholderClassName="bg-core-gray-200"
                      className="h-[50px] w-full rounded bg-gray-500"
                    />
                    <Placeholder
                      placeholderClassName="bg-core-gray-200"
                      className="h-[50px] w-full rounded bg-gray-500"
                    />
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5">
                  <Placeholder
                    placeholderClassName="bg-core-gray-200"
                    className="h-[50px] w-full rounded bg-gray-500"
                  />
                  <Placeholder
                    placeholderClassName="bg-core-gray-200"
                    className="h-[50px] w-full rounded bg-gray-500"
                  />
                  <Placeholder
                    placeholderClassName="bg-core-gray-200"
                    className="h-[50px] w-full rounded bg-gray-500"
                  />
                </div>
              )}
            </div>
          </Else>
        </If>
      </PayItForwardFormProvider>
    </Elements>
  )
}
